import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as m from '@staiter/i18n';
import { type State, type StateColorToken, StateGroup } from '@staiter/shared/task-module';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, GripVertical, Palette } from 'lucide-react';
import { emitToast } from '~/components/feedback/toast-bus';
import { Radio } from '~/components/primitives';
import { patchState } from '~/lib/tasks/api/tasks';
import { STATE_GROUP_LABEL } from '~/lib/tasks/i18n/labels';
import { invalidateStates, projectKeys } from '~/lib/tasks/queryKeys';
import { TaskPopover } from '../inline-editors/TaskPopover';
import { KanbanColorPicker } from '../kanban/KanbanColorPicker';
import { StateChip } from '../primitives/StateChip';
import styles from './StatesManager.module.css';

export type StatesManagerProps = {
  projectId: string;
  states: readonly State[];
};

/**
 * StatesManager — управление состояниями проекта из настроек (FE-28 + FE-27).
 *
 * Drag-reorder (@dnd-kit → patchState sequence), цвет (KanbanColorPicker →
 * color_token), группа (custom-select → group), «по умолчанию» (radio →
 * is_default, BE-6 enforce single-default). Каждый write → toast «Сохранено»
 * (FE-27 явная обратная связь). Изменения влияют на ВСЕ задачи проекта.
 */
export function StatesManager({ projectId, states }: StatesManagerProps): JSX.Element {
  const qc = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const mutate = useMutation({
    mutationFn: (v: { stateId: string; patch: Record<string, unknown> }) =>
      patchState(projectId, v.stateId, v.patch),
    onSuccess: () => {
      emitToast({ variant: 'success', title: m.tm_saved() });
      invalidateStates(qc, projectId);
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
    onError: () => emitToast({ variant: 'error', title: m.tm_save_failed() }),
  });

  const ordered = [...states].sort((a, b) => a.sequence - b.sequence);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = ordered.map((s) => s.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    // Новая sequence перемещённого = sequence соседа в целевой позиции.
    const moved = ordered[from];
    const target = ordered[to];
    if (moved && target) mutate.mutate({ stateId: moved.id, patch: { sequence: target.sequence } });
  };

  return (
    <div className={styles.wrap}>
      <p className={styles.warn}>
        <AlertTriangle size={14} aria-hidden className={styles.warnIcon} />
        {m.tm_states_warn()}
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ordered.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {ordered.map((s) => (
            <StateRow
              key={s.id}
              state={s}
              onColor={(token) => mutate.mutate({ stateId: s.id, patch: { color_token: token } })}
              onGroup={(g) => mutate.mutate({ stateId: s.id, patch: { group: g } })}
              onDefault={() => mutate.mutate({ stateId: s.id, patch: { is_default: true } })}
              saving={mutate.isPending}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

function StateRow({
  state,
  onColor,
  onGroup,
  onDefault,
  saving,
}: {
  state: State;
  onColor: (token: StateColorToken) => void;
  onGroup: (group: string) => void;
  onDefault: () => void;
  saving: boolean;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: state.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={styles.row}>
      <button
        type="button"
        className={styles.grip}
        aria-label={m.tm_move_state_x({ name: state.name })}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} aria-hidden />
      </button>

      <StateChip state={state} />

      <TaskPopover
        width={200}
        trigger={(open) => (
          <button
            type="button"
            className={styles.colorBtn}
            onClick={open}
            aria-label={m.tm_state_color()}
          >
            <Palette size={14} aria-hidden />
          </button>
        )}
      >
        {(close) => (
          <KanbanColorPicker
            value={state.color_token}
            onChange={(t) => {
              onColor(t);
              close();
            }}
          />
        )}
      </TaskPopover>

      <TaskPopover
        width={180}
        trigger={(open) => (
          <button type="button" className={styles.groupBtn} onClick={open}>
            {STATE_GROUP_LABEL[state.group] ?? state.group}
          </button>
        )}
      >
        {(close) => (
          <div className={styles.groupList}>
            {StateGroup.options.map((g) => (
              <button
                key={g}
                type="button"
                className={[styles.groupItem, g === state.group ? styles.groupItemActive : ''].join(
                  ' ',
                )}
                onClick={() => {
                  if (g !== state.group) onGroup(g);
                  close();
                }}
              >
                {STATE_GROUP_LABEL[g]}
              </button>
            ))}
          </div>
        )}
      </TaskPopover>

      {/*
       * F103 (С-I, гейт круга 1, UX MAJOR): «по умолчанию» меняет состояние для ВСЕХ задач проекта —
       * стрелки по общей группе (`name` проекта) писали его на каждое нажатие, а выключение только что
       * выбранной кнопки уводило фокус. Своё имя у каждой строки: стрелки не перескакивают и не пишут;
       * выбор — пробелом или кликом; ни текущая, ни остальные на время записи не выключаются (повтор и
       * выбор во время записи — без запроса). Имя для
       * чтеца называет состояние: подпись «по умолчанию» повторена в каждой строке.
       */}
      <Radio
        className={styles.defaultRadio}
        name={`default-state-${state.id}`}
        checked={state.is_default}
        // Не `disabled` и на время записи: выключение уводило фокус с только что выбранной кнопки.
        onChange={() => {
          if (!saving && !state.is_default) onDefault();
        }}
        aria-label={`${state.name}: ${m.tm_default_lc()}`}
        label={m.tm_default_lc()}
      />
    </div>
  );
}
